import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  Button,
  IconButton,
  Divider,
  useTheme
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import LanguageIcon from '@mui/icons-material/Language';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcherV2 from './LanguageSwitcherV2';

const PublicFooter = () => {
  const theme = useTheme();
  const { t, language, changeLanguage } = useLanguage();
  const currentYear = new Date().getFullYear();

  // Available languages
  const languages = [
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Español' },
    { code: 'pt', label: 'Português' }
  ];

  // Quick links organized by category
  const footerLinks = [
    {
      title: language === 'es' ? 'Enlaces Rápidos' : 'Quick Links',
      links: [
        { label: language === 'es' ? 'Sobre Nosotros' : 'About Us', path: '/nosotros' },
        { label: language === 'es' ? 'Características' : 'Features', path: '/features' },
        { label: language === 'es' ? 'Precios' : 'Pricing', path: '/precios' },
        { label: language === 'es' ? 'Asesoría' : 'Consulting', path: '/consultant' }
      ]
    },
    // 12 ago 2026: la columna "Recursos" (Guías/Blog/Soporte) se quitó de
    // acá junto con el dropdown de PublicHeader.js -- ver esa nota, mismo
    // motivo (ahora vive dentro de la app, no en el sitio público).
  ];

  return (
    <Box
      component="footer"
      sx={{
        py: 6,
        px: 2,
        mt: 'auto',
        backgroundColor: theme.palette.mode === 'light' ? 'grey.100' : 'grey.900',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Logo and description */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
              PCM Finance
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {language === 'es'
                ? 'Sistema de Gestión de Finanzas Personales - Planifique, Monitoree y Controle sus finanzas de manera efectiva.'
                : 'Personal Finance Management System - Plan, Monitor, and Control your finances effectively.'}
            </Typography>

            {/* Social Media */}
            {/* TODO: falta el link real de Facebook e Instagram (Augusto
                los tiene que dar) — sin href todavía, quedan como decoración
                hasta cablearlos. LinkedIn ya tiene la página real de Darwin
                Optima. Se quitó X/Twitter a propósito: no aporta para este
                público (dueños de negocio en Perú). */}
            <Box sx={{ mt: 2 }}>
              <IconButton color="primary" aria-label="facebook">
                <FacebookIcon />
              </IconButton>
              <IconButton
                color="primary"
                aria-label="linkedin"
                component="a"
                href="https://www.linkedin.com/company/darwin-optima/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <LinkedInIcon />
              </IconButton>
              <IconButton color="primary" aria-label="instagram">
                <InstagramIcon />
              </IconButton>
            </Box>

            {/* Se eliminó el selector de idioma para evitar duplicaciones. */}
            {/* Solo se mantiene el selector de idioma en PublicHeader. */}
          </Grid>

          {/* Navigation Links */}
          {footerLinks.map((section) => (
            <Grid item xs={12} sm={6} md={2} key={section.title}>
              <Typography variant="subtitle1" color="text.primary" gutterBottom>
                {section.title}
              </Typography>
              <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {section.links.map((link) => (
                  <Box component="li" key={link.label} sx={{ mb: 1 }}>
                    <Link
                      component={RouterLink}
                      to={link.path}
                      color="text.secondary"
                      underline="hover"
                      sx={{ fontSize: '0.875rem' }}
                    >
                      {link.label}
                    </Link>
                  </Box>
                ))}
              </Box>
            </Grid>
          ))}

          {/* Contact Information */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="subtitle1" color="text.primary" gutterBottom>
              {language === 'es' ? 'Contacto' : 'Contact'}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {language === 'es' ? 'Correo:' : 'Email:'} augusto@darwinoptima.com
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Copyright */}
        <Typography variant="body2" color="text.secondary" align="center">
          © {currentYear} PCM Finance. {language === 'es' ? 'Todos los derechos reservados.' : 'All rights reserved.'}
        </Typography>
      </Container>
    </Box>
  );
};

export default PublicFooter;
